import { useRouter } from 'next/router';

import { useGetProductCartFreeArticleCoupons } from '~/src/common/hooks/cart/useGetCartFreeArticleCoupons';
import Tracker, { CommonCatalogEventProperties } from '~/src/common/services/Tracker';
import { ProductDetailed } from '~/src/common/typings/product';
import { SPECIFIC_TAG_ATTRIBUTE_KEY } from '~/src/common/utils/product';
import { useGetCategories } from '~/src/queries/api-ecom/generated/api-ecom';
import CatalogContentScreenLayout from '~/src/screens/CatalogContent/ContentScreen/CatalogContentScreenLayout';
import useBreadcrumbs from '~/src/screens/CatalogContent/ContentScreen/hooks/useBreadcrumbs';
import { SeoProduct } from '~/src/screens/CatalogContent/ContentScreen/ProductScreen/SeoProduct';
import { prepareSliderImagesFromProduct } from '~/src/screens/CatalogContent/ContentScreen/ProductScreen/utils';
import { CatalogContentLinkQueryParams } from '~/src/screens/CatalogContent/ContentScreen/types';

import * as GS from '../layout';

import { hiddenAttributesKeys, hiddenAttributesValues } from './constants';
import * as S from './layoutOld';
import AdditionalInformation from './ProductAdditionalInformation';
import ProductHeaderPaneOld from './ProductHeaderPaneOld';
import Information from './ProductInformation';

interface Props {
  product: ProductDetailed;
  refetchProduct: () => void;
  analyticsProperties: CommonCatalogEventProperties;
}

const ProductContent = ({ product, refetchProduct, analyticsProperties }: Props) => {
  const { id, images, attributes, name } = product;

  const { data: categories } = useGetCategories();

  const { query } = useRouter<CatalogContentLinkQueryParams>();
  const { category, subcategory } = query;

  const sliderImages = prepareSliderImagesFromProduct(images);

  // Extraction du nombre de produits gratuit et des produits liés
  const freeQuantity = useGetProductCartFreeArticleCoupons(id);

  // Suppression des attributs pas interessants
  const filteredAttributes = attributes.filter(attr => {
    const isEmptyVal = !attr.value;
    const isHiddenKey = [...hiddenAttributesKeys, SPECIFIC_TAG_ATTRIBUTE_KEY].includes(attr.key);
    const isHiddenVal = hiddenAttributesValues.includes(String(attr.value ?? '').toLowerCase());
    const isCustomHidden = attr.key === 'netweight' && attr.valueUnit === 'N/A';
    return !(isEmptyVal || isHiddenKey || isHiddenVal || isCustomHidden);
  });

  const categoriesBreadcrumbs = useBreadcrumbs(categories, product.breadcrumbs);

  const handleImageSliderArrowClick = () => {
    Tracker.sendEvent('roll out photo carousel', {
      'product category': category,
      'product name': name,
      'product subcategory': subcategory,
      'photo event source': 'product page',
      'opened from': 'slider',
    });
  };

  const handleImageSliderThumbnailClick = (eventSource: 'product page' | 'photo modal') => {
    Tracker.sendEvent('roll out photo carousel', {
      'product category': category,
      'product name': name,
      'product subcategory': subcategory,
      'photo event source': eventSource,
      'opened from': 'thumbnails',
    });
  };

  return (
    <>
      <SeoProduct product={product} />
      <CatalogContentScreenLayout item={product} analyticsProperties={analyticsProperties}>
        <S.GridContent>
          <GS.ImageSliderContainer>
            <S.Slider
              images={sliderImages}
              alt={name}
              onArrowClick={handleImageSliderArrowClick}
              onThumbnailClick={handleImageSliderThumbnailClick}
            />
          </GS.ImageSliderContainer>

          <ProductHeaderPaneOld
            product={product}
            freeQuantity={freeQuantity}
            onRefresh={refetchProduct}
            categoriesBreadcrumbs={categoriesBreadcrumbs}
          />

          <Information product={product} />

          <AdditionalInformation attributes={filteredAttributes} />
        </S.GridContent>
      </CatalogContentScreenLayout>
    </>
  );
};

export default ProductContent;
